































































































































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import ExternalAuthMixin from "@/components/app/global/payments/externalAuthMixin.vue";
import Context from "@/mixins/context";

// Enums/Data
import { GatewayTypes, UUID, PaymentActionTypes } from "@/data/enums";
import { BrandConfigKeys } from "@/data/constants";
import { DataModules } from "@/store/modules/data";
import { ConfirmModalModes } from "@/data/enums";

// Types
import { IState } from "@/store";
import { IPaymentDetail } from "@/models/paymentDetails";
import { IGateway } from "@/models/gateways";
import { IClient } from "@/models/clients";
import { IBrand } from "@/models/brands";

@Component({
  computed: {
    ...mapState({
      dataScope(state: IState, getters) {
        return getters[
          `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/clientScope`
        ](this.clientId);
      },
      params(state: IState, getters) {
        return getters[`data/${DataModules.CLIENTS_PAYMENT_DETAILS}/params`](
          this.clientId
        );
      },
      paymentMethods(state, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.CLIENTS_PAYMENT_DETAILS,
          scope: this.dataScope
        });
      }
    })
  }
})
export default class ClientPaymentMethodsList extends Mixins(
  RequestPromises,
  Context,
  ExternalAuthMixin
) {
  // Props
  @Prop({ required: false, default: null })
  public value!: object | null;

  @Prop({ required: false, default: false })
  public activeOnly!: boolean;

  @Prop({ required: true })
  public clientId!: IClient["id"];

  @Prop({ default: false })
  public hasTableHead!: boolean;

  @Prop({ default: UUID.ORG })
  public brandId!: IBrand["id"];

  @Prop({
    required: false,
    default: PaymentActionTypes.LIST,
    validator: value => {
      return _.values(PaymentActionTypes).includes(value) as boolean;
    }
  })
  public action!: string;

  /** isClickable can be used to eg disable a row click when records are staged */

  @Prop({ default: true })
  public isClickable!: boolean;

  public paymentMethods!: IPaymentDetail[];
  public dataScope!: string;
  public dataModule = DataModules.CLIENTS_PAYMENT_DETAILS;
  // Data
  public isLoading = false;
  public requestError = false;
  public isReloading = false;
  public canUpdate: boolean = this.$userCan("update_client_payment_detail");

  public async created() {
    if (!this.$userCan("list_client_payment_details")) return;
    this.isLoading = true;
    this.checkExternalAuthQueryParams();
    this.getPaymentMethods();
  }

  public reloadData() {
    this.isReloading = true;
    this.getPaymentMethods();
  }

  public get brandFilter() {
    return this.brandId !== UUID.ORG ? { brand_id: this.brandId } : {};
  }

  public get mappedPaymentMethods() {
    return this.paymentMethods.map(i => ({
      ...i,
      isVerified: this.isCardGateway(i.gateway) ? this.isVerified(i) : true,
      isVerifiable: this.isCardGateway(i.gateway) ? !this.isVerified(i) : false
    }));
  }

  public get canRemoveLastCard() {
    return !(
      this.$store.getters["brand/config"][
        BrandConfigKeys.PREVENT_CARD_REMOVAL_IF_LAST
      ] ?? false
    );
  }

  public canDelete(method: IPaymentDetail) {
    return method.can_delete && this.$userCan("delete_client_payment_detail");
  }

  public hasAlternativeMethod(methodId = "") {
    const activePaymentMethods = _.filter(
      this.paymentMethods,
      ({ id, active }) => id !== methodId && active
    ).length;

    return methodId === ""
      ? this.paymentMethods.length && activePaymentMethods === 1
      : !!activePaymentMethods;
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CLIENTS_PAYMENT_DETAILS,
      scope: this.dataScope,
      vm: this
    });
  }

  public get activeFilter() {
    return this.activeOnly
      ? {
          activeOnly: true
        }
      : {};
  }

  public isVerified(paymentDetail: IPaymentDetail) {
    if (!paymentDetail?.next_action) return true;
    return paymentDetail?.sca_verified;
  }

  public isStaged(detail: IPaymentDetail) {
    return detail.staged_import;
  }

  public isCardGateway(gateway: IGateway) {
    return gateway.type === GatewayTypes.CARD;
  }

  public verifyPaymentMethod(gateway: IGateway) {
    this.redirectToExternalAuth({
      ...gateway.next_action,
      returnLocation: this.$router.resolve(this.$route.fullPath).location
    });
  }

  public async getPaymentMethods() {
    this.isLoading = true;
    this.requestError = false;
    try {
      await this.$store.dispatch(
        `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/list`,
        {
          vm: this,
          scope: this.dataScope,
          ignoreStored: true,
          ...this.activeFilter,
          id: this.clientId,
          params: {
            limit: 0,
            order: "-default,id",
            with: "gateway"
          }
        }
      );
    } catch (error) {
      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public editPaymentMethod(method: IPaymentDetail) {
    if (!this.canUpdate || this.isStaged(method)) return;
    this.$store.dispatch(
      `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/openEditModal`,
      {
        props: {
          method
        },
        events: {
          "payment-method-updated": this.getPaymentMethods
        }
      }
    );
  }

  public async makeMethodDefault(method: IPaymentDetail) {
    if (!this.canUpdate) return;
    try {
      await this.$store.dispatch(
        `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/update`,
        {
          id: method.client_id,
          payment_details_id: method.id,
          data: {
            default: true
          }
        }
      );
      this.getPaymentMethods();
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }
  }

  public async deletePaymentMethod(method: IPaymentDetail) {
    if (!this.canDelete(method)) return;

    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          mode: ConfirmModalModes.BUTTON,
          autoClose: false,
          action: this.$t("_action.delete_object", {
            object: (this.$t("_.payment_method") as string).toLowerCase()
          }),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.payment_method") as string).toLowerCase()
          })
        },
        canCancel: ["escape", "outside", "button"],
        events: {
          confirmed: async () => {
            try {
              await this.$store.dispatch(
                `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/remove`,
                {
                  id: method.client_id,
                  payment_details_id: method.id
                }
              );
              this.getPaymentMethods();
              this.$buefy.toast.open({
                message: this.$t("_.object_deleted", {
                  object: this.$t("_.payment_method")
                }) as string
              });
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            }
            confirm.close();
          }
        }
      }
    });
  }
}
